import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
} from "lucide-react";
import {
  ASSESSMENT_GOALS,
  CONDITIONS,
  FAMILY_CARDIO,
  FAMILY_NEURO,
  NUTRITION_FREQ_ITEMS,
  FREQ_OPTIONS,
  SLEEP_DURATION,
  TIME_BUCKETS,
} from "../mock";
import { createAssessment } from "../lib/api";

const DRAFT_KEY = "lc_assessment_draft_v1";

const CIRCLES = {
  blue: {
    label: "Blue Longev Circle",
    tagline: "For men",
    accent: "#3E5A7A",
    accentSoft: "#E4EBF3",
  },
  pink: {
    label: "Pink Longev Circle",
    tagline: "For women",
    accent: "#B08585",
    accentSoft: "#F3E4E4",
  },
};

const STEPS = [
  "Circle",
  "Personal",
  "Goals",
  "Medical",
  "Family",
  "Activity",
  "Sleep",
  "Stress",
  "Nutrition",
  "Review",
];

const EMPTY = {
  circle: "",
  personal: {
    name: "",
    email: "",
    phone: "",
    year_of_birth: "",
    height_cm: "",
    weight_kg: "",
    country: "Switzerland",
    language: "English",
    emergency_contact: "",
    primary_hcp: "",
  },
  goals: {
    top_goals: [],
    top_priority: "",
    worthwhile: "",
    confidence: 5,
    time_hours: "",
    preferred_days: [],
    preferred_time: "Morning",
    sports: "",
    coaching_style: "one_on_one",
    gym_membership: "",
    strength_experience: "",
    online_ok: "yes",
  },
  medical: {
    conditions: [],
    other_condition: "",
    medications: [],
    allergies: "",
    surgeries: "",
    current_symptoms: "",
  },
  family: {
    cardio: [],
    diabetes: false,
    cancer: "",
    cancer_age: "",
    neuro: [],
    parents_age_at_death: "",
  },
  activity: {
    movement_hours: "",
    moderate_days: "",
    moderate_minutes: "",
    resistance_days: "",
    daily_steps: "",
    sitting_hours: "",
    bedtime: "",
    early_late_ok: "yes",
    smoking: "never",
    smoking_details: "",
    alcohol_per_week: "",
    alcohol_free_days: "",
    heavy_episodes: "",
    other_notes: "",
  },
  sleep: {
    duration: "7–8h",
    quality: 5,
    difficulty_falling: false,
    wake_frequently: false,
    wake_early: false,
    unrefreshed: false,
    snoring: false,
    breath_pauses: false,
    daytime_sleepy: false,
    uses_meds: false,
    shift_work: false,
  },
  stress: {
    stress_level: 5,
    overwhelmed: "sometimes",
    recovery_time: 5,
    work_life_balance: 5,
  },
  nutrition: {
    frequencies: {},
    breakfast_habit: "",
    late_night_eating: "sometimes",
    eating_out: "sometimes",
    snacking: "sometimes",
    emotional_eating: "sometimes",
    weekend_overeating: "sometimes",
    hydration_liters: "",
    protein_sources: "",
    special_diets: "",
  },
};

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default function AssessmentPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => loadDraft() || EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // persist draft
  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
  }, [form]);

  const patch = (section, values) =>
    setForm((f) => ({ ...f, [section]: { ...f[section], ...values } }));

  const goto = (n) => {
    setStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const next = () => goto(Math.min(STEPS.length - 1, step + 1));
  const back = () => goto(Math.max(0, step - 1));

  const currentCircle = CIRCLES[form.circle] || CIRCLES.blue;

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        name: form.personal.name,
        email: form.personal.email,
        phone: form.personal.phone || null,
        circle: form.circle,
        data: {
          personal: form.personal,
          goals: form.goals,
          medical: form.medical,
          family: form.family,
          activity: form.activity,
          sleep: form.sleep,
          stress: form.stress,
          nutrition: form.nutrition,
        },
      };
      await createAssessment(payload);
      localStorage.removeItem(DRAFT_KEY);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Please check your details and try again.";
      setError(typeof msg === "string" ? msg : "Please check your details.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="pt-[120px] lg:pt-[160px] pb-24 bg-[var(--lc-cream)] min-h-screen">
        <div className="mx-auto max-w-[720px] px-5 lg:px-10 text-center">
          <div
            className="w-16 h-16 rounded-full grid place-items-center mx-auto mb-6"
            style={{ background: currentCircle.accent }}
          >
            <CheckCircle2 className="h-7 w-7 text-white" />
          </div>
          <div className="eyebrow text-[var(--lc-moss)] mb-4">Assessment received</div>
          <h1 className="font-serif-display text-[clamp(36px,5vw,68px)] leading-[1.05] font-light">
            Thank you, {form.personal.name.split(" ")[0]}.
            <br />
            <span className="italic">Your care team is on it.</span>
          </h1>
          <p className="mt-7 text-[15px] leading-relaxed text-[var(--lc-muted)]">
            Our clinical and coaching team will review your questionnaire and reach out
            within 48 hours at <span className="text-[var(--lc-ink)] font-semibold">{form.personal.email}</span> to
            schedule your assessment visit and lab work.
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-10 inline-flex items-center gap-2 px-6 py-3 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors"
          >
            Back to Home <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-[100px] lg:pt-[110px] pb-24 bg-[var(--lc-cream)] min-h-screen">
      {/* Progress bar */}
      {step > 0 && (
        <div className="mx-auto max-w-[840px] px-5 lg:px-10 mb-8">
          <div className="flex items-center justify-between text-[10px] tracking-[0.2em] uppercase text-[var(--lc-muted)] mb-3">
            <span>
              Step {step} of {STEPS.length - 1}
            </span>
            <span className="font-semibold" style={{ color: currentCircle.accent }}>
              {currentCircle.label}
            </span>
          </div>
          <div className="h-1 bg-[var(--lc-line)] rounded-full overflow-hidden">
            <div
              className="h-full transition-all duration-500 rounded-full"
              style={{
                width: `${(step / (STEPS.length - 1)) * 100}%`,
                background: currentCircle.accent,
              }}
            />
          </div>
        </div>
      )}

      <div className="mx-auto max-w-[840px] px-5 lg:px-10">
        {step === 0 && (
          <StepCircle
            value={form.circle}
            onChange={(v) => setForm((f) => ({ ...f, circle: v }))}
            onNext={() => form.circle && next()}
          />
        )}
        {step === 1 && (
          <StepPersonal
            value={form.personal}
            onChange={(v) => patch("personal", v)}
            accent={currentCircle.accent}
          />
        )}
        {step === 2 && (
          <StepGoals
            value={form.goals}
            onChange={(v) => patch("goals", v)}
            circle={form.circle}
            accent={currentCircle.accent}
          />
        )}
        {step === 3 && (
          <StepMedical
            value={form.medical}
            onChange={(v) => patch("medical", v)}
            accent={currentCircle.accent}
          />
        )}
        {step === 4 && (
          <StepFamily
            value={form.family}
            onChange={(v) => patch("family", v)}
            accent={currentCircle.accent}
          />
        )}
        {step === 5 && (
          <StepActivity
            value={form.activity}
            onChange={(v) => patch("activity", v)}
            accent={currentCircle.accent}
          />
        )}
        {step === 6 && (
          <StepSleep
            value={form.sleep}
            onChange={(v) => patch("sleep", v)}
            accent={currentCircle.accent}
          />
        )}
        {step === 7 && (
          <StepStress
            value={form.stress}
            onChange={(v) => patch("stress", v)}
            accent={currentCircle.accent}
          />
        )}
        {step === 8 && (
          <StepNutrition
            value={form.nutrition}
            onChange={(v) => patch("nutrition", v)}
            accent={currentCircle.accent}
          />
        )}
        {step === 9 && (
          <StepReview form={form} accent={currentCircle.accent} />
        )}

        {/* Nav buttons */}
        {step > 0 && (
          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <button
              onClick={back}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white border border-[var(--lc-line)] text-[var(--lc-ink)] text-[12px] font-bold tracking-[0.18em] uppercase hover:border-[var(--lc-ink)] transition-colors order-2 sm:order-1"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                onClick={next}
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 text-white text-[12px] font-bold tracking-[0.18em] uppercase hover:opacity-90 transition-opacity order-1 sm:order-2"
                style={{ background: currentCircle.accent }}
              >
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting || !form.personal.name || !form.personal.email}
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors order-1 sm:order-2 disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Submit Assessment <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {error && (
          <div className="mt-6 flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        {step > 0 && (
          <p className="mt-8 text-[11px] text-[var(--lc-muted)] tracking-wide leading-relaxed">
            Your answers are saved automatically as you go. Nothing here is a diagnosis
            or medical advice — a licensed physician on our team will review your
            responses before your assessment visit.
          </p>
        )}
      </div>
    </section>
  );
}

/* ------------------- Reusable primitives ------------------- */

function SectionHeader({ eyebrow, title, italic, note, accent }) {
  return (
    <div className="mb-10">
      <div className="eyebrow mb-4" style={{ color: accent || "var(--lc-moss)" }}>
        {eyebrow}
      </div>
      <h1 className="font-serif-display text-[clamp(32px,4.5vw,56px)] leading-[1.05] font-light">
        {title} {italic && <span className="italic">{italic}</span>}
      </h1>
      {note && (
        <p className="mt-5 text-[15px] leading-relaxed text-[var(--lc-muted)] max-w-xl">
          {note}
        </p>
      )}
    </div>
  );
}

function Card({ children }) {
  return (
    <div className="bg-white border border-[var(--lc-line)] rounded-md p-6 lg:p-8">
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="eyebrow text-[var(--lc-muted)] mb-2 block">{label}</label>
      {children}
      {hint && <p className="mt-1.5 text-xs text-[var(--lc-muted)]">{hint}</p>}
    </div>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className={`w-full px-4 py-3 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-[14px] focus:outline-none focus:border-[var(--lc-ink)] ${props.className || ""}`}
    />
  );
}

function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full px-4 py-3 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-[14px] focus:outline-none focus:border-[var(--lc-ink)] resize-none ${props.className || ""}`}
    />
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-[14px] focus:outline-none focus:border-[var(--lc-ink)]"
    >
      {options.map((o) => (
        <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
          {typeof o === "string" ? o : o.label}
        </option>
      ))}
    </select>
  );
}

function Pills({ options, value, onChange, accent }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const val = typeof o === "string" ? o : o.value;
        const label = typeof o === "string" ? o : o.label;
        const active = value === val;
        return (
          <button
            key={val}
            type="button"
            onClick={() => onChange(val)}
            className={`px-4 py-2 text-[12px] font-semibold tracking-[0.14em] uppercase border transition-colors ${
              active
                ? "text-white"
                : "bg-white text-[var(--lc-ink)] border-[var(--lc-line)] hover:border-[var(--lc-ink)]"
            }`}
            style={active ? { background: accent, borderColor: accent } : undefined}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function ChipMulti({ options, values, onChange, accent, max }) {
  const toggle = (v) => {
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else if (!max || values.length < max) onChange([...values, v]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const val = typeof o === "string" ? o : o.value;
        const label = typeof o === "string" ? o : o.label;
        const active = values.includes(val);
        return (
          <button
            key={val}
            type="button"
            onClick={() => toggle(val)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold tracking-[0.14em] uppercase border transition-colors ${
              active ? "text-white" : "bg-white text-[var(--lc-ink)] border-[var(--lc-line)] hover:border-[var(--lc-ink)]"
            }`}
            style={active ? { background: accent, borderColor: accent } : undefined}
          >
            {active && <Check className="h-3 w-3" strokeWidth={3} />}
            {label}
          </button>
        );
      })}
    </div>
  );
}

function Slider({ value, onChange, min = 0, max = 10, accent }) {
  return (
    <div>
      <div className="flex items-center gap-4">
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 accent-current"
          style={{ accentColor: accent }}
        />
        <span
          className="min-w-[52px] text-center font-serif-display text-3xl leading-none"
          style={{ color: accent }}
        >
          {value}
        </span>
      </div>
      <div className="flex justify-between text-[11px] text-[var(--lc-muted)] mt-1">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}

function YesNo({ value, onChange, accent }) {
  return (
    <Pills options={[{ value: true, label: "Yes" }, { value: false, label: "No" }]} value={value} onChange={onChange} accent={accent} />
  );
}

/* ------------------- Steps ------------------- */

function StepCircle({ value, onChange, onNext }) {
  return (
    <div>
      <div className="text-center max-w-2xl mx-auto mb-10 lg:mb-14">
        <div className="eyebrow text-[var(--lc-moss)] mb-4">Longevity Assessment</div>
        <h1 className="font-serif-display text-[clamp(36px,6vw,72px)] leading-[1] font-light">
          Where are you today?
          <br />
          <span className="italic">Let's find out.</span>
        </h1>
        <p className="mt-6 text-[15px] leading-relaxed text-[var(--lc-muted)]">
          A premium baseline assessment that measures your health, capability and
          lifestyle — and turns it into a personalized 3–6 month action plan.
          Takes 15–25 minutes online. No commitment.
        </p>
      </div>

      <div className="text-center mb-6">
        <div className="eyebrow text-[var(--lc-muted)]">Start by choosing your circle</div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {Object.entries(CIRCLES).map(([k, c]) => {
          const active = value === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => onChange(k)}
              className={`relative text-left rounded-md p-6 lg:p-8 transition-all ${
                active
                  ? "border-2 shadow-lg -translate-y-0.5"
                  : "border border-[var(--lc-line)] hover:border-[var(--lc-ink)]"
              }`}
              style={{
                background: active ? c.accentSoft : "white",
                borderColor: active ? c.accent : undefined,
              }}
            >
              <div
                className="w-11 h-11 rounded-full grid place-items-center mb-5"
                style={{ background: c.accent }}
              >
                {active ? <Check className="h-5 w-5 text-white" strokeWidth={3} /> : <span className="w-3 h-3 rounded-full bg-white" />}
              </div>
              <div className="eyebrow" style={{ color: c.accent }}>
                {c.tagline}
              </div>
              <div className="font-serif-display text-3xl leading-tight mt-1">
                {c.label}
              </div>
              <p className="mt-3 text-[13px] text-[var(--lc-muted)] leading-relaxed">
                Tailored questionnaire, physician review and program specific to your biology.
              </p>
            </button>
          );
        })}
      </div>

      <div className="mt-10 text-center">
        <button
          disabled={!value}
          onClick={onNext}
          className="inline-flex items-center gap-2 px-8 py-4 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Begin Assessment <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      <p className="mt-8 text-center text-[11px] text-[var(--lc-muted)] tracking-wide">
        Your answers stay private. Nothing here is a diagnosis — a licensed physician
        reviews every submission.
      </p>
    </div>
  );
}

function StepPersonal({ value, onChange, accent }) {
  return (
    <div>
      <SectionHeader
        eyebrow="A. Personal information"
        title="Let's start with"
        italic="the basics."
        note="These details help us match you with the right physician and schedule your assessment visit."
        accent={accent}
      />
      <Card>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Full name *">
            <Input value={value.name} onChange={(e) => onChange({ name: e.target.value })} />
          </Field>
          <Field label="Email *">
            <Input type="email" value={value.email} onChange={(e) => onChange({ email: e.target.value })} />
          </Field>
          <Field label="Phone / WhatsApp">
            <Input value={value.phone} onChange={(e) => onChange({ phone: e.target.value })} />
          </Field>
          <Field label="Year of birth">
            <Input inputMode="numeric" placeholder="e.g. 1978" value={value.year_of_birth} onChange={(e) => onChange({ year_of_birth: e.target.value })} />
          </Field>
          <Field label="Height (cm)">
            <Input inputMode="numeric" value={value.height_cm} onChange={(e) => onChange({ height_cm: e.target.value })} />
          </Field>
          <Field label="Weight (kg)">
            <Input inputMode="numeric" value={value.weight_kg} onChange={(e) => onChange({ weight_kg: e.target.value })} />
          </Field>
          <Field label="Country of residence">
            <Input value={value.country} onChange={(e) => onChange({ country: e.target.value })} />
          </Field>
          <Field label="Preferred language">
            <Select value={value.language} onChange={(v) => onChange({ language: v })} options={["English", "Deutsch", "Français", "Italiano", "Hindi", "Other"]} />
          </Field>
          <Field label="Emergency contact" hint="Name and phone number">
            <Input value={value.emergency_contact} onChange={(e) => onChange({ emergency_contact: e.target.value })} />
          </Field>
          <Field label="Primary care physician (optional)">
            <Input value={value.primary_hcp} onChange={(e) => onChange({ primary_hcp: e.target.value })} />
          </Field>
        </div>
      </Card>
    </div>
  );
}

function StepGoals({ value, onChange, circle, accent }) {
  const availableGoals = ASSESSMENT_GOALS.filter(
    (g) => g.applies === "all" || g.applies === circle
  );

  const toggleGoal = (id) => {
    const has = value.top_goals.includes(id);
    if (has) onChange({ top_goals: value.top_goals.filter((x) => x !== id), top_priority: value.top_priority === id ? "" : value.top_priority });
    else if (value.top_goals.length < 5) onChange({ top_goals: [...value.top_goals, id] });
  };

  const dayToggle = (d) => {
    const has = value.preferred_days.includes(d);
    onChange({
      preferred_days: has ? value.preferred_days.filter((x) => x !== d) : [...value.preferred_days, d],
    });
  };

  return (
    <div>
      <SectionHeader
        eyebrow="B. Goals"
        title="Where do you want"
        italic="to end up?"
        note="This section shapes your entire program. Be honest — top 5 goals, then pick your #1 priority."
        accent={accent}
      />
      <div className="space-y-5">
        <Card>
          <Field label={`Top 5 health goals for the next 6 months  ·  ${value.top_goals.length}/5 selected`}>
            <div className="grid gap-2 mt-2">
              {availableGoals.map((g) => {
                const active = value.top_goals.includes(g.id);
                const full = value.top_goals.length >= 5 && !active;
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => toggleGoal(g.id)}
                    disabled={full}
                    className={`flex items-start gap-3 text-left px-4 py-3 border rounded-sm transition-colors ${
                      active
                        ? "text-white"
                        : full
                        ? "bg-[var(--lc-cream-soft)] border-[var(--lc-line)] opacity-50 cursor-not-allowed"
                        : "bg-white border-[var(--lc-line)] hover:border-[var(--lc-ink)]"
                    }`}
                    style={active ? { background: accent, borderColor: accent } : undefined}
                  >
                    <span
                      className={`mt-0.5 grid place-items-center w-4 h-4 rounded-sm shrink-0 border ${
                        active ? "bg-white border-white" : "border-[var(--lc-line)]"
                      }`}
                    >
                      {active && <Check className="h-3 w-3" style={{ color: accent }} strokeWidth={3.5} />}
                    </span>
                    <span className="text-[14px] leading-snug">{g.label}</span>
                  </button>
                );
              })}
            </div>
          </Field>
        </Card>

        {value.top_goals.length > 0 && (
          <Card>
            <Field label="Which one is your #1 priority?">
              <div className="grid gap-2">
                {value.top_goals.map((id) => {
                  const g = ASSESSMENT_GOALS.find((x) => x.id === id);
                  const active = value.top_priority === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => onChange({ top_priority: id })}
                      className={`flex items-center gap-3 text-left px-4 py-3 border rounded-sm transition-colors ${
                        active ? "text-white" : "bg-white border-[var(--lc-line)] hover:border-[var(--lc-ink)]"
                      }`}
                      style={active ? { background: accent, borderColor: accent } : undefined}
                    >
                      <span
                        className={`w-4 h-4 rounded-full shrink-0 border ${active ? "bg-white border-white" : "border-[var(--lc-line)]"}`}
                      />
                      <span className="text-[14px]">{g?.label}</span>
                    </button>
                  );
                })}
              </div>
            </Field>
          </Card>
        )}

        <Card>
          <Field label="What would make this assessment worthwhile for you?" hint="A sentence or two is perfect.">
            <Textarea
              rows={3}
              value={value.worthwhile}
              onChange={(e) => onChange({ worthwhile: e.target.value })}
            />
          </Field>
        </Card>

        <Card>
          <div className="grid lg:grid-cols-2 gap-6">
            <Field label="Confidence in making lifestyle changes over 6 months (0–10)">
              <Slider value={value.confidence} onChange={(v) => onChange({ confidence: v })} accent={accent} />
            </Field>
            <Field label="Realistic time available for health each week">
              <Pills options={TIME_BUCKETS} value={value.time_hours} onChange={(v) => onChange({ time_hours: v })} accent={accent} />
            </Field>
          </div>
        </Card>

        <Card>
          <div className="grid gap-6">
            <Field label="Preferred days for coaching sessions">
              <div className="flex flex-wrap gap-2">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => {
                  const active = value.preferred_days.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => dayToggle(d)}
                      className={`w-12 h-10 text-xs font-bold tracking-wider border transition-colors ${
                        active ? "text-white" : "bg-white text-[var(--lc-ink)] border-[var(--lc-line)] hover:border-[var(--lc-ink)]"
                      }`}
                      style={active ? { background: accent, borderColor: accent } : undefined}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Preferred time of day">
              <Pills options={["Morning", "Lunchtime", "Evening", "Weekend"]} value={value.preferred_time} onChange={(v) => onChange({ preferred_time: v })} accent={accent} />
            </Field>
            <Field label="Do you play any sport or physical activity (dance, yoga, pilates)? What frequency?">
              <Textarea rows={2} value={value.sports} onChange={(e) => onChange({ sports: e.target.value })} />
            </Field>
          </div>
        </Card>

        <Card>
          <div className="grid gap-6">
            <Field label="Do you prefer group sessions or 1:1 coaching?">
              <Pills
                options={[
                  { value: "group", label: "Group" },
                  { value: "one_on_one", label: "1:1" },
                  { value: "mix", label: "Mix" },
                ]}
                value={value.coaching_style}
                onChange={(v) => onChange({ coaching_style: v })}
                accent={accent}
              />
            </Field>
            <Field label="Existing gym / fitness club membership?">
              <Input placeholder="e.g. Basel Fitness, or 'no'" value={value.gym_membership} onChange={(e) => onChange({ gym_membership: e.target.value })} />
            </Field>
            <Field label="Experience with strength training (weights or calisthenics)?">
              <Pills
                options={[
                  { value: "none", label: "None" },
                  { value: "some", label: "Some" },
                  { value: "regular", label: "Regular" },
                  { value: "advanced", label: "Advanced" },
                ]}
                value={value.strength_experience}
                onChange={(v) => onChange({ strength_experience: v })}
                accent={accent}
              />
            </Field>
            <Field label="Comfortable with online sessions? Do you have bands, dumbbells, etc.?">
              <Textarea
                rows={2}
                placeholder="Describe your home setup or preferences"
                value={value.online_ok}
                onChange={(e) => onChange({ online_ok: e.target.value })}
              />
            </Field>
          </div>
        </Card>
      </div>
    </div>
  );
}

function StepMedical({ value, onChange, accent }) {
  const toggleCondition = (c) => {
    const has = value.conditions.includes(c);
    onChange({
      conditions: has ? value.conditions.filter((x) => x !== c) : [...value.conditions, c],
    });
  };
  const addMed = () =>
    onChange({ medications: [...value.medications, { name: "", dose: "", frequency: "", reason: "" }] });
  const updateMed = (i, field, val) => {
    const next = [...value.medications];
    next[i] = { ...next[i], [field]: val };
    onChange({ medications: next });
  };
  const removeMed = (i) =>
    onChange({ medications: value.medications.filter((_, idx) => idx !== i) });

  return (
    <div>
      <SectionHeader
        eyebrow="C. Medical history"
        title="Your medical"
        italic="context."
        note="This section is reviewed by a licensed physician. Nothing here is auto-interpreted."
        accent={accent}
      />
      <div className="space-y-5">
        <Card>
          <Field label="Have you ever been diagnosed with any of the following?">
            <div className="flex flex-wrap gap-2 mt-1">
              {CONDITIONS.map((c) => {
                const active = value.conditions.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCondition(c)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-[12px] font-semibold border transition-colors ${
                      active ? "text-white" : "bg-white text-[var(--lc-ink)] border-[var(--lc-line)] hover:border-[var(--lc-ink)]"
                    }`}
                    style={active ? { background: accent, borderColor: accent } : undefined}
                  >
                    {active && <Check className="h-3 w-3" strokeWidth={3} />}
                    {c}
                  </button>
                );
              })}
            </div>
          </Field>
          <div className="mt-5">
            <Field label="Other significant condition (optional)">
              <Input value={value.other_condition} onChange={(e) => onChange({ other_condition: e.target.value })} />
            </Field>
          </div>
        </Card>

        <Card>
          <div className="flex items-end justify-between mb-4">
            <div className="eyebrow text-[var(--lc-muted)]">Current medications</div>
            <button
              type="button"
              onClick={addMed}
              className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.18em] uppercase"
              style={{ color: accent }}
            >
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          </div>
          {value.medications.length === 0 && (
            <p className="text-sm text-[var(--lc-muted)]">No medications listed yet.</p>
          )}
          <div className="space-y-3">
            {value.medications.map((m, i) => (
              <div key={i} className="grid sm:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-center">
                <Input placeholder="Name" value={m.name} onChange={(e) => updateMed(i, "name", e.target.value)} />
                <Input placeholder="Dose" value={m.dose} onChange={(e) => updateMed(i, "dose", e.target.value)} />
                <Input placeholder="Frequency" value={m.frequency} onChange={(e) => updateMed(i, "frequency", e.target.value)} />
                <Input placeholder="Reason" value={m.reason} onChange={(e) => updateMed(i, "reason", e.target.value)} />
                <button
                  type="button"
                  onClick={() => removeMed(i)}
                  className="p-2 text-[var(--lc-muted)] hover:text-[var(--lc-ink)]"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div className="grid gap-5">
            <Field label="Known allergies or intolerances">
              <Textarea rows={2} value={value.allergies} onChange={(e) => onChange({ allergies: e.target.value })} />
            </Field>
            <Field label="Previous surgeries or major injuries">
              <Textarea rows={2} value={value.surgeries} onChange={(e) => onChange({ surgeries: e.target.value })} />
            </Field>
            <Field label="Current symptoms or health concerns you want the clinician to know">
              <Textarea rows={3} value={value.current_symptoms} onChange={(e) => onChange({ current_symptoms: e.target.value })} />
            </Field>
          </div>
        </Card>
      </div>
    </div>
  );
}

function StepFamily({ value, onChange, accent }) {
  return (
    <div>
      <SectionHeader
        eyebrow="D. Family history"
        title="First-degree"
        italic="relatives."
        note="Parents, siblings and children. Used only for context — never for a simplistic prediction."
        accent={accent}
      />
      <div className="space-y-5">
        <Card>
          <div className="grid gap-6">
            <Field label="Cardiovascular history in family">
              <ChipMulti options={FAMILY_CARDIO} values={value.cardio} onChange={(v) => onChange({ cardio: v })} accent={accent} />
            </Field>
            <Field label="Type 2 Diabetes in family?">
              <YesNo value={value.diabetes} onChange={(v) => onChange({ diabetes: v })} accent={accent} />
            </Field>
            <Field label="Major cancers in family (type, if known)">
              <Input value={value.cancer} onChange={(e) => onChange({ cancer: e.target.value })} />
            </Field>
            <Field label="Age at diagnosis (if known)">
              <Input inputMode="numeric" value={value.cancer_age} onChange={(e) => onChange({ cancer_age: e.target.value })} />
            </Field>
            <Field label="Neurodegenerative disease in family">
              <ChipMulti options={FAMILY_NEURO} values={value.neuro} onChange={(v) => onChange({ neuro: v })} accent={accent} />
            </Field>
            <Field label="Age at death of parents (optional)">
              <Input value={value.parents_age_at_death} onChange={(e) => onChange({ parents_age_at_death: e.target.value })} placeholder="e.g. Father 78, Mother alive" />
            </Field>
          </div>
        </Card>
      </div>
    </div>
  );
}

function StepActivity({ value, onChange, accent }) {
  return (
    <div>
      <SectionHeader
        eyebrow="E. Activity, smoking & alcohol"
        title="How you"
        italic="move & live."
        note="No judgment here — this is just data for your coach and physician."
        accent={accent}
      />
      <div className="space-y-5">
        <Card>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Typical movement hours per day">
              <Input inputMode="decimal" value={value.movement_hours} onChange={(e) => onChange({ movement_hours: e.target.value })} />
            </Field>
            <Field label="Days per week of moderate/vigorous exercise">
              <Input inputMode="numeric" value={value.moderate_days} onChange={(e) => onChange({ moderate_days: e.target.value })} />
            </Field>
            <Field label="Average minutes per exercise session">
              <Input inputMode="numeric" value={value.moderate_minutes} onChange={(e) => onChange({ moderate_minutes: e.target.value })} />
            </Field>
            <Field label="Days per week of resistance training">
              <Input inputMode="numeric" value={value.resistance_days} onChange={(e) => onChange({ resistance_days: e.target.value })} />
            </Field>
            <Field label="Average daily steps (if known)">
              <Input inputMode="numeric" value={value.daily_steps} onChange={(e) => onChange({ daily_steps: e.target.value })} />
            </Field>
            <Field label="Hours per day sitting">
              <Input inputMode="numeric" value={value.sitting_hours} onChange={(e) => onChange({ sitting_hours: e.target.value })} />
            </Field>
            <Field label="Usual bedtime">
              <Input placeholder="e.g. 23:00" value={value.bedtime} onChange={(e) => onChange({ bedtime: e.target.value })} />
            </Field>
            <Field label="OK with early morning or late evening sessions?">
              <Pills
                options={[
                  { value: "yes", label: "Yes" },
                  { value: "morning_only", label: "Morning only" },
                  { value: "evening_only", label: "Evening only" },
                  { value: "no", label: "No" },
                ]}
                value={value.early_late_ok}
                onChange={(v) => onChange({ early_late_ok: v })}
                accent={accent}
              />
            </Field>
          </div>
        </Card>

        <Card>
          <div className="grid gap-6">
            <Field label="Smoking status">
              <Pills
                options={[
                  { value: "never", label: "Never" },
                  { value: "former", label: "Former" },
                  { value: "current", label: "Current" },
                ]}
                value={value.smoking}
                onChange={(v) => onChange({ smoking: v })}
                accent={accent}
              />
            </Field>
            {value.smoking !== "never" && (
              <Field label="Cigarettes per day / years smoked / quit date (as relevant)">
                <Input value={value.smoking_details} onChange={(e) => onChange({ smoking_details: e.target.value })} />
              </Field>
            )}
          </div>
        </Card>

        <Card>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Alcoholic drinks per week (typical)">
              <Input inputMode="numeric" value={value.alcohol_per_week} onChange={(e) => onChange({ alcohol_per_week: e.target.value })} />
            </Field>
            <Field label="Alcohol-free days per week">
              <Input inputMode="numeric" value={value.alcohol_free_days} onChange={(e) => onChange({ alcohol_free_days: e.target.value })} />
            </Field>
            <Field label="Heavy-episode frequency">
              <Pills
                options={["Never", "Rare", "Monthly", "Weekly"]}
                value={value.heavy_episodes}
                onChange={(v) => onChange({ heavy_episodes: v })}
                accent={accent}
              />
            </Field>
          </div>
        </Card>

        <Card>
          <Field label="Any other lifestyle condition worth sharing?">
            <Textarea rows={3} value={value.other_notes} onChange={(e) => onChange({ other_notes: e.target.value })} />
          </Field>
        </Card>
      </div>
    </div>
  );
}

function StepSleep({ value, onChange, accent }) {
  const bools = [
    ["difficulty_falling", "Difficulty falling asleep"],
    ["wake_frequently", "Wake frequently during the night"],
    ["wake_early", "Wake too early"],
    ["unrefreshed", "Feel unrefreshed in the morning"],
    ["snoring", "Snore loudly"],
    ["breath_pauses", "Someone has observed pauses in my breathing"],
    ["daytime_sleepy", "Excessive daytime sleepiness"],
    ["uses_meds", "Use sleep medication or supplements"],
    ["shift_work", "Shift work or frequent travel"],
  ];
  return (
    <div>
      <SectionHeader eyebrow="F. Sleep & recovery" title="How you" italic="recover." accent={accent} />
      <div className="space-y-5">
        <Card>
          <div className="grid gap-6">
            <Field label="Average sleep duration">
              <Pills options={SLEEP_DURATION} value={value.duration} onChange={(v) => onChange({ duration: v })} accent={accent} />
            </Field>
            <Field label="Sleep quality (0–10)">
              <Slider value={value.quality} onChange={(v) => onChange({ quality: v })} accent={accent} />
            </Field>
          </div>
        </Card>
        <Card>
          <div className="eyebrow text-[var(--lc-muted)] mb-3">Which of the following apply to you?</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {bools.map(([k, label]) => {
              const active = value[k];
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => onChange({ [k]: !active })}
                  className={`flex items-center gap-3 text-left px-4 py-3 border rounded-sm transition-colors ${
                    active ? "text-white" : "bg-white border-[var(--lc-line)] hover:border-[var(--lc-ink)]"
                  }`}
                  style={active ? { background: accent, borderColor: accent } : undefined}
                >
                  <span
                    className={`grid place-items-center w-4 h-4 rounded-sm border ${active ? "bg-white border-white" : "border-[var(--lc-line)]"}`}
                  >
                    {active && <Check className="h-3 w-3" style={{ color: accent }} strokeWidth={3.5} />}
                  </span>
                  <span className="text-[14px] leading-snug">{label}</span>
                </button>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}

function StepStress({ value, onChange, accent }) {
  return (
    <div>
      <SectionHeader eyebrow="G. Stress & wellbeing" title="How you" italic="feel." accent={accent} />
      <Card>
        <div className="grid gap-8">
          <Field label="Current stress level (0–10)">
            <Slider value={value.stress_level} onChange={(v) => onChange({ stress_level: v })} accent={accent} />
          </Field>
          <Field label="How often do you feel overwhelmed by responsibilities?">
            <Pills
              options={["never", "sometimes", "often", "almost always"]}
              value={value.overwhelmed}
              onChange={(v) => onChange({ overwhelmed: v })}
              accent={accent}
            />
          </Field>
          <Field label="How often do you feel you have adequate recovery time? (0–10)">
            <Slider value={value.recovery_time} onChange={(v) => onChange({ recovery_time: v })} accent={accent} />
          </Field>
          <Field label="Work/life balance satisfaction (0–10)">
            <Slider value={value.work_life_balance} onChange={(v) => onChange({ work_life_balance: v })} accent={accent} />
          </Field>
        </div>
      </Card>
    </div>
  );
}

function StepNutrition({ value, onChange, accent }) {
  const setFreq = (item, freq) =>
    onChange({ frequencies: { ...value.frequencies, [item]: freq } });

  const behaviorFields = [
    ["breakfast_habit", "Typical breakfast", ["Skip", "Light", "Balanced", "Heavy"]],
    ["late_night_eating", "Late-night eating"],
    ["eating_out", "Eating out"],
    ["snacking", "Snacking"],
    ["emotional_eating", "Emotional eating"],
    ["weekend_overeating", "Weekend overeating"],
  ];
  const freqBehavior = ["never", "sometimes", "often", "almost always"];

  return (
    <div>
      <SectionHeader
        eyebrow="H. Nutrition"
        title="How you"
        italic="fuel."
        note="No food diary needed. Our nutritionist will go deeper if you enter the 6-month program."
        accent={accent}
      />
      <div className="space-y-5">
        <Card>
          <div className="eyebrow text-[var(--lc-muted)] mb-4">How often do you consume:</div>
          <div className="space-y-3">
            {NUTRITION_FREQ_ITEMS.map((item) => (
              <div key={item} className="grid lg:grid-cols-[180px_1fr] gap-3 items-start">
                <div className="text-sm font-semibold pt-2">{item}</div>
                <Pills options={FREQ_OPTIONS} value={value.frequencies[item] || ""} onChange={(v) => setFreq(item, v)} accent={accent} />
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div className="eyebrow text-[var(--lc-muted)] mb-4">Eating behaviors</div>
          <div className="grid sm:grid-cols-2 gap-5">
            {behaviorFields.map(([key, label, options]) => (
              <Field key={key} label={label}>
                <Pills options={options || freqBehavior} value={value[key]} onChange={(v) => onChange({ [key]: v })} accent={accent} />
              </Field>
            ))}
          </div>
        </Card>

        <Card>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Approximate fluid intake (litres/day)">
              <Input inputMode="decimal" value={value.hydration_liters} onChange={(e) => onChange({ hydration_liters: e.target.value })} />
            </Field>
            <Field label="Main protein sources">
              <Input placeholder="e.g. chicken, eggs, tofu, whey" value={value.protein_sources} onChange={(e) => onChange({ protein_sources: e.target.value })} />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Special diets, restrictions, cultural preferences (optional)">
              <Textarea rows={2} value={value.special_diets} onChange={(e) => onChange({ special_diets: e.target.value })} />
            </Field>
          </div>
        </Card>
      </div>
    </div>
  );
}

function StepReview({ form, accent }) {
  const goalLabels = form.goals.top_goals
    .map((id) => ASSESSMENT_GOALS.find((g) => g.id === id)?.label)
    .filter(Boolean);
  const priorityLabel = ASSESSMENT_GOALS.find((g) => g.id === form.goals.top_priority)?.label;

  return (
    <div>
      <SectionHeader
        eyebrow="Almost done"
        title="Review &"
        italic="submit."
        note="Your care team will review this within 48 hours and reach out to schedule your assessment visit."
        accent={accent}
      />
      <div className="space-y-4">
        <SummaryCard title="Circle & Personal">
          <SummaryRow label="Circle" value={CIRCLES[form.circle]?.label} />
          <SummaryRow label="Name" value={form.personal.name} />
          <SummaryRow label="Email" value={form.personal.email} />
          <SummaryRow label="Phone" value={form.personal.phone} />
          <SummaryRow label="Year of birth" value={form.personal.year_of_birth} />
          <SummaryRow label="Height / Weight" value={`${form.personal.height_cm || "—"} cm · ${form.personal.weight_kg || "—"} kg`} />
        </SummaryCard>
        <SummaryCard title="Goals">
          <SummaryRow label="#1 priority" value={priorityLabel} />
          <SummaryRow label="Goals selected" value={goalLabels.join(" · ")} />
          <SummaryRow label="Time per week" value={form.goals.time_hours} />
          <SummaryRow label="Coaching style" value={form.goals.coaching_style} />
        </SummaryCard>
        <SummaryCard title="Medical">
          <SummaryRow label="Conditions" value={form.medical.conditions.join(", ") || "None reported"} />
          <SummaryRow label="Medications" value={form.medical.medications.length ? `${form.medical.medications.length} listed` : "None"} />
        </SummaryCard>
        <SummaryCard title="Activity & Lifestyle">
          <SummaryRow label="Exercise days/wk" value={form.activity.moderate_days} />
          <SummaryRow label="Smoking" value={form.activity.smoking} />
          <SummaryRow label="Alcohol/wk" value={form.activity.alcohol_per_week} />
        </SummaryCard>
        <SummaryCard title="Sleep & Stress">
          <SummaryRow label="Sleep duration" value={form.sleep.duration} />
          <SummaryRow label="Sleep quality" value={`${form.sleep.quality}/10`} />
          <SummaryRow label="Stress" value={`${form.stress.stress_level}/10`} />
        </SummaryCard>
      </div>
    </div>
  );
}

function SummaryCard({ title, children }) {
  return (
    <div className="bg-white border border-[var(--lc-line)] rounded-md p-6">
      <div className="eyebrow text-[var(--lc-moss)] mb-3">{title}</div>
      <dl className="divide-y divide-[var(--lc-line)]">{children}</dl>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="py-2 grid grid-cols-[140px_1fr] gap-3 items-baseline">
      <dt className="text-xs tracking-[0.16em] uppercase text-[var(--lc-muted)]">{label}</dt>
      <dd className="text-sm text-[var(--lc-ink)]">{value || <span className="text-[var(--lc-muted)]">—</span>}</dd>
    </div>
  );
}
